import { Chip, FormHelperText, IconButton, TextField } from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Plus, X } from "@/shared/foundation/icons";
import { Button } from "@/shared/foundation/primitives";

import {
  collapseEnumValueWhitespace,
  forbiddenCharacterIn,
} from "./createCustomFieldForm";
import styles from "./EnumOptionsEditor.module.css";

/** The slice of the create and edit forms this editor reads and writes. */
interface EnumOptionRows {
  enum_values: { value: string }[];
}

interface EnumOptionsEditorProps {
  /**
   * Options the field already has, shown as fixed chips ahead of the editable rows. Values can
   * be added to an existing field but never removed, since entities may already hold them.
   */
  existingValues?: string[];
}

/**
 * Editor for an enum field's allowed values. Renders one text input per option
 * with add/remove controls. Validation (non-empty, unique, length, cap) lives in
 * the form schema and surfaces inline per row.
 *
 * Each input tidies its own whitespace when it loses focus, so the extra spaces the section
 * text promises to remove are visibly gone before the options are saved -- afterwards they
 * can no longer be changed.
 */
export function EnumOptionsEditor({
  existingValues = [],
}: EnumOptionsEditorProps) {
  const { control, register, formState, setValue } =
    useFormContext<EnumOptionRows>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "enum_values",
  });

  return (
    <div className={styles.section}>
      <span className={styles.label}>Enum Options</span>
      <FormHelperText>
        Options can be added later but not removed. Extra spaces are removed.
      </FormHelperText>
      {existingValues.length > 0 ? (
        <div className={styles.existing}>
          {existingValues.map((value) => (
            <Chip key={value} label={value} size="small" variant="outlined" />
          ))}
        </div>
      ) : null}
      <div className={styles.rows}>
        {fields.map((field, index) => {
          const error = formState.errors.enum_values?.[index]?.value;
          const option = register(`enum_values.${index}.value`);
          return (
            <div key={field.id} className={styles.row}>
              <TextField
                placeholder={`Option ${existingValues.length + index + 1}`}
                size="small"
                fullWidth
                error={Boolean(error)}
                helperText={error?.message}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    // Add another option instead of submitting the form.
                    event.preventDefault();
                    append({ value: "" });
                  }
                }}
                {...option}
                // Replaces the onBlur `{...option}` just spread, so the form's own
                // handler has to be called by hand.
                onBlur={(event) => {
                  const typed = event.target.value;
                  void option.onBlur(event);

                  // A value carrying a forbidden character is left exactly as typed:
                  // rewriting it would turn the tab this rejects into the space it accepts,
                  // silently resolving an error the user was just asked to fix.
                  if (forbiddenCharacterIn(typed) !== undefined) {
                    return;
                  }

                  const tidied = collapseEnumValueWhitespace(typed);
                  if (tidied !== typed) {
                    setValue(`enum_values.${index}.value`, tidied, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                }}
              />
              {/* A field always keeps at least one option, so a new field's last row stays. */}
              {existingValues.length + fields.length > 1 ? (
                <IconButton
                  className={styles.remove}
                  aria-label={`Remove option ${existingValues.length + index + 1}`}
                  size="small"
                  onClick={() => remove(index)}
                >
                  <X size={16} />
                </IconButton>
              ) : null}
            </div>
          );
        })}
      </div>
      <Button
        className={styles.addButton}
        variant="text"
        startIcon={<Plus size={16} />}
        onClick={() => append({ value: "" })}
      >
        Add option
      </Button>
    </div>
  );
}
